import { makeCsrfFetch } from './api';
import { showToast } from './toast';

interface ProductImage {
    url: string;
    alt_text?: string;
}

interface CartLine {
    id: string;
    product_title: string;
    merchandise_id: string;
    merchandise_title: string;
    formatted_price: string;
    formatted_total_amount: string;
    quantity: number;
    product_image: ProductImage | null;
}

interface Cart {
    id: string;
    lines: CartLine[];
    total_cost: string;
    checkout_url: string | null;
}

const rawPageData = document.getElementById('pageData');
if (rawPageData === null) {
    throw new Error('pageData element is null');
}
const pageData = JSON.parse(rawPageData.textContent);
const csrfToken = pageData.csrfToken;
const csrfFetch = makeCsrfFetch(csrfToken);
let currentCart: Cart | null = null;
let isCartOpen = false;
let scrollPosition = 0;

// Debounce management for cart updates
const pendingUpdates = new Map<string, number>();
const DEBOUNCE_DELAY = 500; // ms


function toggleCartView(open: boolean): void {
    const cartContainer = document.querySelector<HTMLElement>('.js-cart-container');
    if (cartContainer === null) {
        throw new Error('.js-cart-container element is null');
    }
    const cartOverlay = document.querySelector<HTMLElement>('.js-cart-overlay');
    if (cartOverlay === null) {
        throw new Error('.js-cart-overlay element is null');
    }
    const header = document.querySelector<HTMLElement>('.header');
    if (header === null) {
        throw new Error('.header element is null');
    }

    if (isCartOpen === open) { return; }
    isCartOpen = open;

    if (open) {
        // Preserve scroll position and freeze page/header when using position:fixed (see html.no-scroll in CSS)
        scrollPosition = window.pageYOffset;
        document.body.style.top = `-${scrollPosition}px`;
        document.documentElement.classList.add('no-scroll');
        header.style.top = '0';
        header.style.transform = 'none';
        cartContainer.classList.add('active');
        cartOverlay.classList.add('active');
    } else {
        // Restore scroll position and header state
        document.documentElement.classList.remove('no-scroll');
        document.body.style.top = '';
        header.style.top = '';
        header.style.transform = '';
        window.scrollTo(0, scrollPosition);
        cartContainer.classList.remove('active');
        cartOverlay.classList.remove('active');
    }
}

function updateCartCount(): void {
    const headerCartCount = document.getElementById('headerCartCount');
    let totalItems = 0;
    if (currentCart && currentCart.lines) {
        totalItems = currentCart.lines.reduce((sum, line) => sum + line.quantity, 0);
    }
    if (headerCartCount) {
        if (totalItems > 0) {
            headerCartCount.textContent = totalItems.toString();
            headerCartCount.setAttribute('data-count', totalItems.toString());
            headerCartCount.style.display = 'flex';
        } else {
            headerCartCount.style.display = 'none';
        }
    }
}

function createCartItemElement(item: CartLine): HTMLElement {
    const itemDiv = document.createElement('div');
    itemDiv.className = 'cart-item';

    const featuredImage = item.product_image;

    // Create image element
    let imageHTML = '';
    if (featuredImage) {
        imageHTML = `
      <img src="${featuredImage.url}" 
           alt="${featuredImage.alt_text || item.product_title}" 
           class="cart-item-image"
           onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
      <div class="cart-item-image" style="display: none;"></div>
    `;
    } else {
        imageHTML = '<div class="cart-item-image"></div>';
    }

    // Only show variant title if it's not the default single variant
    const showVariant = item.merchandise_title && item.merchandise_title !== 'Default Title';

    itemDiv.innerHTML = `
    ${imageHTML}
    <div class="cart-item-details">
      <div class="cart-item-title-row">
        <div class="cart-item-title">${item.product_title}</div>
        <button class="cart-item__remove-btn js-cart-remove-btn">Remove</button>
      </div>
      ${showVariant ? `<div class="cart-item-variant">${item.merchandise_title}</div>` : ''}
      <div class="cart-item-price-and-controls">
        <div class="cart-item-price">${item.formatted_total_amount}</div>
        <div class="cart-item__quantity-controls">
          <button class="cart-item__quantity-btn cart-item__quantity-btn--minus" data-action="decrease">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 8H12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <span class="quantity-display">${item.quantity}</span>
          <button class="cart-item__quantity-btn" data-action="increase">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 4V12M4 8H12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  `;

    // Attach event listeners
    const removeBtn = itemDiv.querySelector<HTMLButtonElement>('.js-cart-remove-btn');
    if (removeBtn === null) {
        throw new Error('.js-cart-remove-btn element is null');
    }
    // eslint-disable-next-line no-use-before-define
    removeBtn.addEventListener('click', () => removeFromCart(item.id));

    const increaseBtn = itemDiv.querySelector<HTMLButtonElement>('[data-action="increase"]');
    if (increaseBtn === null) {
        throw new Error('[data-action="increase"] element is null');
    }
    // eslint-disable-next-line no-use-before-define
    increaseBtn.addEventListener('click', () => updateQuantity(item.id, item.quantity + 1));

    const decreaseBtn = itemDiv.querySelector<HTMLButtonElement>('[data-action="decrease"]');
    if (decreaseBtn === null) {
        throw new Error('[data-action="decrease"] element is null');
    }
    // eslint-disable-next-line no-use-before-define
    decreaseBtn.addEventListener('click', () => updateQuantity(item.id, item.quantity - 1));

    return itemDiv;
}

function renderCart(): void {
    const container = document.getElementById('cart-container');
    if (container === null) {
        throw new Error('cart-container element is null');
    }
    const checkoutSection = document.getElementById('checkout-section');

    if (!currentCart || currentCart.lines.length === 0) {
        container.innerHTML = '<div class="cart-content__empty">Your cart is empty</div>';
        if (checkoutSection) { checkoutSection.style.display = 'none'; }
        return;
    }

    // Clear container
    container.innerHTML = '';

    // Create and append each cart item
    currentCart.lines.forEach(item => {
        const itemElement = createCartItemElement(item);
        container.appendChild(itemElement);
    });

    if (checkoutSection) {
        checkoutSection.style.display = 'flex';

        // Update or create total display
        let totalDisplay = checkoutSection.querySelector('.cart-total');
        if (!totalDisplay) {
            totalDisplay = document.createElement('div');
            totalDisplay.className = 'cart-total';
            totalDisplay.innerHTML = `
        <div class="cart-total__label">Cart Total:</div>
        <div class="cart-total__amount"></div>
      `;
            checkoutSection.insertBefore(totalDisplay, checkoutSection.firstChild);
        }

        // Update total amount
        const totalAmount = totalDisplay.querySelector('.cart-total__amount');
        if (totalAmount) {
            totalAmount.textContent = currentCart.total_cost;
        }
    }
}

function setCheckoutButtonLoading(loading: boolean): void {
    const checkoutBtn = document.getElementById('checkout-btn') as HTMLButtonElement;
    if (!checkoutBtn) { return; }

    if (loading) {
        checkoutBtn.disabled = true;
        checkoutBtn.classList.add('loading');
    } else {
        checkoutBtn.disabled = false;
        checkoutBtn.classList.remove('loading');
    }
}

function updateCartState(cart: Cart | null): void {
    currentCart = cart;
    renderCart();
    updateCartCount();
}

export async function addToCart(variantId: number, shopId: number, quantity: number = 1): Promise<boolean> {
    if (!variantId) {
        showToast('Please select a variant');
        return false;
    }

    // Convert variantId to Shopify GID format for comparison
    const variantGid = `gid://shopify/ProductVariant/${variantId}`;
    const existingLine = currentCart?.lines.find((line: CartLine) =>
        line.merchandise_id === variantGid
    );
    const existingQuantity = existingLine?.quantity || 0;
    const expectedQuantity = existingQuantity + quantity;

    try {
        // Concurrent update handling: Server processes requests sequentially, so even if 
        // one update fails, the final cart state will contain all successful updates.
        // Failed updates will revert from their optimistic values to actual server state.
        const response = await csrfFetch('/cart/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                variant_id: variantId,
                quantity: quantity,
                shop_id: shopId,
            }),
        });

        if (!response.ok) {
            showToast('Something went wrong, please try again later');
            return false;
        }

        // The add endpoint wraps the cart as { cart, added_quantity }, unlike the
        // update endpoint which returns the cart directly.
        const data = await response.json();
        const cart = data.cart;
        // Check actual quantity after adding
        const updatedLine = cart.lines.find((line: CartLine) =>
            line.merchandise_id === variantGid
        );

        if (updatedLine && updatedLine.quantity < expectedQuantity) {
            showToast('Added to cart (limited quantity)');
        } else {
            showToast('Item added successfully');
        }

        updateCartState(cart);
        return true;
    } catch {
        showToast('Something went wrong, please try again later');
        return false;
    }
}

async function removeFromCart(lineId: string): Promise<void> {
    try {
        const response = await csrfFetch('/cart/', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                line_id: lineId,
            }),
        });

        if (response.status === 404 || response.status === 410) {
            showToast('Your cart is no longer available. Please add items again.');
            updateCartState(null);
            return;
        }

        if (!response.ok) {
            showToast('Something went wrong, please try again later');
            return;
        }

        const data = await response.json();
        showToast('Item removed successfully');
        updateCartState(data);
    } catch {
        // Ignore initialization failures and keep the page interactive.
    }
}

function updateQuantity(lineId: string, newQuantity: number): void {
    if (!currentCart) { return; }

    if (newQuantity <= 0) {
        removeFromCart(lineId);
        return;
    }

    const line = currentCart.lines.find((l: CartLine) => l.id === lineId);
    if (!line) { return; }

    line.quantity = newQuantity;

    renderCart();
    updateCartCount();
    setCheckoutButtonLoading(true);

    if (pendingUpdates.has(lineId)) {
        clearTimeout(pendingUpdates.get(lineId)!);
    }

    const timer = setTimeout(async () => {
        pendingUpdates.delete(lineId);

        try {
            const response = await csrfFetch('/cart/', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    line_id: lineId,
                    quantity: newQuantity,
                }),
            });

            if (response.status === 404 || response.status === 410) {
                showToast('Your cart is no longer available. Please add items again.');
                updateCartState(null);
                return;
            }

            if (!response.ok) {
                showToast('Something went wrong, please try again later');
                return;
            }

            const data = await response.json();
            const updatedLine = data.lines.find((cartLine: CartLine) => cartLine.id === lineId);
            if (updatedLine && updatedLine.quantity !== newQuantity) {
                showToast(`Quantity adjusted to ${updatedLine.quantity} (limited stock)`);
            }

            // Only update cart state when all updates are done
            if (pendingUpdates.size === 0) {
                updateCartState(data);
            }
        } catch {
            showToast('Something went wrong, please try again later');
        } finally {
            // Check if unlock checkout button
            if (pendingUpdates.size === 0) {
                setCheckoutButtonLoading(false);
            }
        }
    }, DEBOUNCE_DELAY);

    pendingUpdates.set(lineId, timer);
}

function proceedToCheckout(): void {
    if (!currentCart || currentCart.lines.length === 0) {
        return;
    }
    if (currentCart.checkout_url === null) {
        return;
    }

    const checkoutBtn = document.getElementById('checkout-btn') as HTMLButtonElement;
    if (checkoutBtn) {
        checkoutBtn.disabled = true;
        checkoutBtn.textContent = 'Redirecting to Checkout...';
    }

    window.location.href = currentCart.checkout_url;
}

async function initializeCart(): Promise<void> {
    const headerCartIcon = document.querySelector<HTMLElement>('.js-header-cart-icon');
    if (headerCartIcon === null) {
        throw new Error('.js-header-cart-icon element is null');
    }
    const closeBtn = document.querySelector<HTMLElement>('.js-cart-close-btn');
    if (closeBtn === null) {
        throw new Error('.js-cart-close-btn element is null');
    }
    const cartOverlay = document.querySelector<HTMLElement>('.js-cart-overlay');
    if (cartOverlay === null) {
        throw new Error('.js-cart-overlay element is null');
    }

    headerCartIcon.addEventListener('click', () => toggleCartView(true));
    closeBtn.addEventListener('click', () => toggleCartView(false));
    cartOverlay.addEventListener('click', () => toggleCartView(false));

    try {
        const response = await csrfFetch('/cart/');

        if (response.status === 204) {
            updateCartState(null);
        } else if (response.ok) {
            const data = await response.json();
            updateCartState(data);
        }
    } catch {
        showToast('Something went wrong, please try again later');
    }
}

export async function initCartComponent(): Promise<void> {
    await initializeCart();

    const checkoutBtn = document.querySelector<HTMLElement>('.js-checkout-btn');
    if (checkoutBtn) {
        checkoutBtn.addEventListener('click', proceedToCheckout);
    }
}
